/**
 * Phase 5B: 9-card KPI strip on the detail-page Overview section.
 *
 * Order — Total Return, CAGR, Net P&L, Win Rate, Sharpe Ratio, Max Drawdown,
 * Profit Factor, Total Trades, Capital Deployed.
 *
 * Renders `—` for any null KPI. Win Rate / Sharpe / Sortino carry a `(n=X)`
 * confidence suffix when the sample is below the platform's confidence
 * threshold. Max Drawdown shows the aggregate value with the worst-subscriber
 * value as a sub-line in smaller font. Profit Factor uses the backend's
 * pre-formatted `profit_factor_display` (`>10` clamp, `<0.1` clamp, raw
 * `1.23` otherwise; `—` when no trades).
 *
 * Every label tooltip cites the Metric Definitions appendix entry.
 */
import React from 'react';
import type {
  MarketplaceBasketKpi,
} from '../../types/marketplaceAnalytics';
import Tooltip from '../ui/Tooltip';
import { cn } from '../../utils/cn';
import { formatInr, formatPct } from '../../utils/platformFormat';

interface Props {
  kpi: MarketplaceBasketKpi | undefined;
}

const DASH = '—';

const fmtNumber = (n: number | null | undefined, digits = 2): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
};

const fmtCount = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return Math.round(n).toLocaleString('en-IN');
};

const fmtPercent = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return formatPct(n);
};

interface CardProps {
  label: string;
  tooltip: string;
  value: string;
  subValue?: string;
  tone?: 'pos' | 'neg' | 'neutral';
  badge?: string;
}

const Card: React.FC<CardProps> = ({
  label, tooltip, value, subValue, tone = 'neutral', badge,
}) => {
  const toneClass =
    tone === 'pos' ? 'text-green-600 dark:text-green-400'
    : tone === 'neg' ? 'text-red-600 dark:text-red-400'
    : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <Tooltip text={tooltip}>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-help">
          {label}{badge ? <span className="ml-1 text-amber-600">{badge}</span> : null}
        </div>
      </Tooltip>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>
        {value}
      </div>
      {subValue && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
          {subValue}
        </div>
      )}
    </div>
  );
};

const tone = (n: number | null | undefined): 'pos' | 'neg' | 'neutral' => {
  if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return 'neutral';
  return n > 0 ? 'pos' : 'neg';
};

const MarketplaceKpiStrip: React.FC<Props> = ({ kpi }) => {
  if (!kpi) return null;

  const winRateBadge = kpi.win_rate_low_confidence ? `(n=${kpi.win_rate_n ?? 0})` : undefined;
  const sharpeBadge = kpi.sharpe_low_confidence ? `(n=${kpi.sharpe_n ?? 0})` : undefined;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
      <Card
        label="Total Return"
        tooltip="Cumulative capital-weighted return across the window. Excludes subscribers with fewer than 7 active days."
        value={
          kpi.total_return === null || kpi.total_return === undefined
            ? DASH
            : `${(kpi.total_return * 100).toFixed(2)}%`
        }
        tone={tone(kpi.total_return ?? null)}
      />
      <Card
        label="CAGR"
        tooltip="Subscriber-time-weighted annualised return: weighted average of (end/start)^(365/days) − 1 across eligible subscribers."
        value={
          kpi.cagr === null || kpi.cagr === undefined
            ? DASH
            : `${(kpi.cagr * 100).toFixed(2)}%`
        }
        subValue={
          kpi.cagr_excluded_subscribers
            ? `${kpi.cagr_excluded_subscribers} excluded`
            : undefined
        }
        tone={tone(kpi.cagr ?? null)}
      />
      <Card
        label="Net P&L"
        tooltip="Cumulative net P&L across the basket — sum of all subscribers' realised P&L for this basket over the window."
        value={fmtNumber(kpi.net_pnl, 2)}
        tone={tone(kpi.net_pnl)}
      />
      <Card
        label="Win Rate"
        tooltip="Wins / (wins + losses) across decided trades in the window. Confidence badge appears below the 30-trade threshold."
        value={fmtPercent(kpi.win_rate)}
        badge={winRateBadge}
      />
      <Card
        label="Sharpe Ratio"
        tooltip="Annualised: (mean daily return / pstdev daily return) × √252. Below 30 days flagged low-confidence."
        value={fmtNumber(kpi.sharpe_ratio, 2)}
        badge={sharpeBadge}
      />
      <Card
        label="Max Drawdown"
        tooltip="Peak-to-trough decline of the NAV-style cumulative equity curve. Worst-subscriber DD shown below."
        value={
          kpi.max_drawdown_aggregate === null
          || kpi.max_drawdown_aggregate === undefined
            ? DASH
            : `${(kpi.max_drawdown_aggregate * 100).toFixed(2)}%`
        }
        subValue={
          kpi.max_drawdown_worst_subscriber !== null
          && kpi.max_drawdown_worst_subscriber !== undefined
            ? `worst sub: ${(kpi.max_drawdown_worst_subscriber * 100).toFixed(2)}%`
            : undefined
        }
        tone="neg"
      />
      <Card
        label="Profit Factor"
        tooltip="Gross profit / gross loss across the window. Clamped to >10 / <0.1 at display; — when zero trades."
        value={kpi.profit_factor_display || DASH}
      />
      <Card
        label="Total Trades"
        tooltip="Total decided trades in the window across all subscribers (wins + losses + breakeven)."
        value={fmtCount(kpi.trades_total)}
      />
      <Card
        label="Capital Deployed"
        tooltip="Sum of capital_per_set × lot_multiplier across active subscribers as of the latest rollup day."
        value={formatInr(kpi.capital_deployed ?? null)}
      />
    </div>
  );
};

export default MarketplaceKpiStrip;
